import { nextIsoDate, todayIso } from '@/lib/calendar/calendarDates'

/**
 * Development-only demo schedule for visual QA.
 * Enabled only when VITE_CALENDAR_DEMO_DATA=true — never in production by default.
 *
 * @param {string} dateIso
 * @param {import('@/types/api').Stylist[]} stylists
 * @returns {import('@/types/api').Appointment[]}
 */
export function buildDemoAppointments(dateIso, stylists) {
  const day = dateIso || todayIso()
  const active = stylists.filter((stylist) => stylist.is_active).slice(0, 4)

  if (active.length === 0) {
    return []
  }

  const [sara, layla, noor, fatima] = [
    active[0],
    active[1] ?? active[0],
    active[2] ?? active[0],
    active[3] ?? active[1] ?? active[0],
  ]

  /** @type {Array<{ stylist: import('@/types/api').Stylist, start: string, end: string, name: string, service: string, status: string, category: string }>} */
  const seeds = [
    {
      stylist: sara,
      start: '10:30',
      end: '11:30',
      name: 'Sarah Ahmed',
      service: 'Blow Dry',
      status: 'confirmed',
      category: 'Hair',
    },
    {
      stylist: sara,
      start: '12:00',
      end: '13:00',
      name: 'Aisha Mohammed',
      service: 'Haircut + Style',
      status: 'checked_in',
      category: 'Hair',
    },
    {
      stylist: layla,
      start: '11:00',
      end: '12:00',
      name: 'Mary Khalid',
      service: 'Classic Manicure',
      status: 'confirmed',
      category: 'Nails',
    },
    {
      stylist: layla,
      start: '11:30',
      end: '12:30',
      name: 'Nora Al Hashimi',
      service: 'Gel Overlay',
      status: 'scheduled',
      category: 'Nails',
    },
    {
      stylist: noor,
      start: '10:00',
      end: '11:00',
      name: 'Hessa Al Maktoum',
      service: 'Hydrating Facial',
      status: 'in_chair',
      category: 'Facial',
    },
    {
      stylist: noor,
      start: '14:00',
      end: '15:30',
      name: 'Fatima Saeed',
      service: 'Bridal Makeup Trial',
      status: 'confirmed',
      category: 'Makeup',
    },
    {
      stylist: fatima,
      start: '13:00',
      end: '14:00',
      name: 'Layla Hassan',
      service: 'Balayage Consult',
      status: 'completed',
      category: 'Colour',
    },
    {
      stylist: fatima,
      start: '15:00',
      end: '16:00',
      name: 'Mariam Yousef',
      service: 'Keratin Treatment',
      status: 'confirmed',
      category: 'Hair',
    },
  ]

  return seeds.map((seed, index) => {
    const duration =
      Number(seed.end.split(':')[0]) * 60 +
      Number(seed.end.split(':')[1]) -
      (Number(seed.start.split(':')[0]) * 60 + Number(seed.start.split(':')[1]))

    return {
      id: 9000 + index,
      branch_id: seed.stylist.branch_id ?? 1,
      customer_id: 9000 + index,
      stylist_id: seed.stylist.id,
      status: seed.status,
      source: 'walk_in',
      starts_at: `${day}T${seed.start}:00`,
      ends_at: `${day}T${seed.end}:00`,
      duration_minutes: duration,
      notes: null,
      customer: { id: 9000 + index, name: seed.name, phone: '+971500000000' },
      stylist: { id: seed.stylist.id, name: seed.stylist.name },
      services: [
        {
          id: 9000 + index,
          service_id: 9000 + index,
          duration_minutes: duration,
          service: {
            id: 9000 + index,
            name: seed.service,
            commission_category: seed.category.toLowerCase(),
            category: { id: 1, name: seed.category },
          },
        },
      ],
    }
  })
}

/**
 * @returns {import('@/types/api').Stylist[]}
 */
export function buildDemoStylists() {
  return [
    {
      id: 101,
      branch_id: 1,
      name: 'Sara Al Mansoori',
      skills: ['Hair Stylist'],
      colour: '#8A6D42',
      bookable_online: true,
      is_active: true,
    },
    {
      id: 102,
      branch_id: 1,
      name: 'Layla Ahmed',
      skills: ['Nail Artist'],
      colour: '#6B7A8A',
      bookable_online: true,
      is_active: true,
    },
    {
      id: 103,
      branch_id: 1,
      name: 'Noor Khalid',
      skills: ['Facial Specialist'],
      colour: '#6B8A7A',
      bookable_online: true,
      is_active: true,
    },
    {
      id: 104,
      branch_id: 1,
      name: 'Fatima Saeed',
      skills: ['Colourist'],
      colour: '#7A6B8A',
      bookable_online: true,
      is_active: true,
    },
  ]
}

/**
 * @param {string} from
 * @param {string} to
 */
export function demoRangeCovered(from, to) {
  const start = from
  const end = to || nextIsoDate(from)
  return { from: start, to: end }
}
